import React from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Alert, Slide, Snackbar, useMediaQuery } from '@mui/material'
import GlobalTip from '../providers/GlobalTip'
import CurrentClientProvider from '../providers/CurrentClientProvider'
import AuthProvider from '../providers/AuthProvider'
import V3ShellHeader from '../components/layout/V3ShellHeader'
import { V3ShellLayoutProvider } from '../components/layout/V3ShellLayoutContext'
import V3LeftDrawer from '../components/layout/V3LeftDrawer'
import ScrollToTopFab from '../components/layout/ScrollToTopFab'
import V3IndieChatPanel from '../components/orbit/v3/V3IndieChatPanel'
import type { OrbitChatViewHandoff } from '../hooks/useOrbitChat'
import { shouldHydrateClientContext } from '../components/layout/ClientMiddleware'
import ClientFeatureFlagService, { AI_CHAT_BOT_FEATURE_FLAG } from '../services/ClientFeatureFlagService'
import { trackEvent } from '../services/AmplitudeService'
import {
  buildV3AdminPermissionsPath,
  buildLegacyClientDashboardPath,
  buildV3AdminUserRolesPath,
  buildV3ClientDashboardPath,
  buildV3ClientDashboardPerformancePath,
  buildV3ClientIndieAiPath,
  buildV3ClientDashboardReportsPath,
  buildV3ClientCategoriesPath,
  buildV3ClientClassesPath,
  buildV3ClientPosMappingPath,
  buildV3UserDetailPath,
  buildV3ClientLocationsPath,
  buildV3ClientProfilePath,
  buildV3ClientUploadDataPath,
  buildV3UsersPath,
  getClientDashboardV3Enabled,
  shouldBypassCrossClientDashboard,
} from '../services/ClientDashboardV3'
import {
  ORBIT_OPEN_INDIE_PANEL_EVENT,
  getOrbitPageAssistantState,
  subscribeOrbitPageAssistantState,
} from '../services/orbit/orbitPageAssistantBridge'

function MessageTransitionDown(props: any) {
  return <Slide {...props} direction="down" />
}

const PERSISTENT_LEFT_NAV_WIDTH = 88

function getShouldShowV3NavToggle(authContext: any, currentUser: any) {
  const roleTags = Array.isArray(currentUser?.roleTags)
    ? currentUser.roleTags.map((tag: string | number) => String(tag))
    : []

  const isPlannerRole = roleTags.includes('2')
  const isManagementOneRole = roleTags.includes('d76bf050-3555-4a15-8e0a-3f6c57eb31a7')

  return Boolean(
    isPlannerRole
    || isManagementOneRole
    || authContext?.checkPermission?.([
      'role-create',
      'full-planning',
      'add-client',
      'view-client-info',
      'dev-tools',
    ]),
  )
}

function getV3SettingsMenuItems(authContext: any) {
  const can = (permission: string) => Boolean(authContext?.checkPermission?.(permission))

  return [
    can('user-page-show') && { key: 'users', label: 'Users', to: buildV3UsersPath() },
    can('role-assign-user') && { key: 'user-roles', label: 'User Roles', to: buildV3AdminUserRolesPath() },
    can('permission-page-show') && { key: 'permissions', label: 'Roles & Permissions', to: buildV3AdminPermissionsPath() },
    can('permission-page-show') && { key: 'flight-control', label: 'Flight Control', to: '/v3/admin/flightcontrol' },
  ].filter(Boolean)
}

export default function V3SiteLayout() {
  const { client_code } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const authContext = AuthProvider.useAuth() as any
  const globalTipContext = GlobalTip.useMessage() as any
  const currentClientContext = CurrentClientProvider.useClient() as any
  const isMobile = useMediaQuery('(max-width:767.95px)')
  const currentClient = currentClientContext?.client
  const currentUser = authContext?.user
  const bypassCrossClientDashboard = shouldBypassCrossClientDashboard(currentUser)
  const showPlannerLeftNav = !bypassCrossClientDashboard && getShouldShowV3NavToggle(authContext, currentUser)
  const hasPersistentLeftNav = Boolean(authContext?.checkPermission?.('role-create')) && !isMobile
  const showShellNavToggle = !bypassCrossClientDashboard && (isMobile || (showPlannerLeftNav && !hasPersistentLeftNav))
  const shouldRenderLeftDrawer = !bypassCrossClientDashboard && (showPlannerLeftNav || isMobile)
  const shouldShowHydrationState = shouldHydrateClientContext(currentClient, client_code)
  const isV3EnabledForClient = getClientDashboardV3Enabled(currentClient, currentUser)
  const [navOpen, setNavOpen] = React.useState(false)
  const [indiePanelMode, setIndiePanelMode] = React.useState<'closed' | 'flyout' | 'docked' | 'page'>('closed')
  const [indiePanelPromptRequest, setIndiePanelPromptRequest] = React.useState<{ id: number; prompt: string; submit: boolean } | null>(null)
  const [indiePanelBypassFeatureFlag, setIndiePanelBypassFeatureFlag] = React.useState(false)
  const indiePanelPromptSequence = React.useRef(0)
  const [pageAssistant, setPageAssistant] = React.useState(() => getOrbitPageAssistantState())

  const resolvedClientCode = currentClient?.ClientCode || client_code || ''
  const resolvedClientId = currentClient?.Id ?? currentClient?.id
  const embeddedFeatureFlagsMap = React.useMemo(
    () => ClientFeatureFlagService.normalizeFeatureFlagsMap(currentClient),
    [currentClient],
  )
  const hasEmbeddedAiChatFlag = Object.prototype.hasOwnProperty.call(embeddedFeatureFlagsMap, AI_CHAT_BOT_FEATURE_FLAG)
  const embeddedAiChatValue = embeddedFeatureFlagsMap[AI_CHAT_BOT_FEATURE_FLAG]
  const [isAiChatBotEnabled, setIsAiChatBotEnabled] = React.useState<boolean | null>(
    resolvedClientId ? (hasEmbeddedAiChatFlag ? Boolean(embeddedAiChatValue) : null) : false,
  )
  const aiChatFlagRequestRef = React.useRef<Promise<boolean> | null>(null)
  const currentClientRef = React.useRef(currentClient)
  const currentClientContextRef = React.useRef(currentClientContext)
  const embeddedFeatureFlagsMapRef = React.useRef(embeddedFeatureFlagsMap)
  const hasEmbeddedAiChatFlagRef = React.useRef(hasEmbeddedAiChatFlag)
  const embeddedAiChatValueRef = React.useRef(embeddedAiChatValue)
  const isFullPageChatRoute = /^\/v3\/client\/[^/]+\/indie-ai$/.test(location.pathname)
  const activeIndiePanelMode = isFullPageChatRoute ? 'closed' : indiePanelMode
  const indiePanelWidth = 460

  React.useEffect(() => {
    trackEvent('V3 Navigation Event', {
      area: 'client',
      action: 'Page Viewed',
      clientCode: resolvedClientCode || 'none',
      path: location.pathname,
      search: location.search || '',
    })
  }, [location.pathname, location.search, resolvedClientCode])

  currentClientRef.current = currentClient
  currentClientContextRef.current = currentClientContext
  embeddedFeatureFlagsMapRef.current = embeddedFeatureFlagsMap
  hasEmbeddedAiChatFlagRef.current = hasEmbeddedAiChatFlag
  embeddedAiChatValueRef.current = embeddedAiChatValue

  const resolveAiChatBotEnabled = React.useCallback((): Promise<boolean> => {
    if (!resolvedClientId) {
      setIsAiChatBotEnabled(false)
      return Promise.resolve(false)
    }

    if (hasEmbeddedAiChatFlagRef.current) {
      const enabled = Boolean(embeddedAiChatValueRef.current)
      setIsAiChatBotEnabled(enabled)
      return Promise.resolve(enabled)
    }

    if (aiChatFlagRequestRef.current) {
      return aiChatFlagRequestRef.current
    }

    const request = ClientFeatureFlagService.getFlags(resolvedClientId)
      .then((flags) => {
        const enabled = Boolean(flags?.[AI_CHAT_BOT_FEATURE_FLAG])
        const latestCurrentClient = currentClientRef.current

        setIsAiChatBotEnabled(enabled)
        if (latestCurrentClient?.ClientCode) {
          currentClientContextRef.current?.setClient?.({
            ...latestCurrentClient,
            featureFlagsMap: {
              ...embeddedFeatureFlagsMapRef.current,
              ...flags,
            },
          })
        }

        return enabled
      })
      .catch(() => {
        setIsAiChatBotEnabled(false)
        return false
      })
      .finally(() => {
        aiChatFlagRequestRef.current = null
      })

    aiChatFlagRequestRef.current = request
    return request
  }, [resolvedClientId])

  React.useEffect(() => {
    let cancelled = false

    if (!resolvedClientId) {
      setIsAiChatBotEnabled(false)
      return () => {
        cancelled = true
      }
    }

    if (hasEmbeddedAiChatFlag) {
      setIsAiChatBotEnabled(Boolean(embeddedAiChatValue))
      return () => {
        cancelled = true
      }
    }

    setIsAiChatBotEnabled(null)
    resolveAiChatBotEnabled()
      .then((enabled) => {
        if (cancelled) return

        setIsAiChatBotEnabled(enabled)
      })
      .catch(() => {
        if (cancelled) return
        setIsAiChatBotEnabled(false)
      })

    return () => {
      cancelled = true
    }
  }, [embeddedAiChatValue, hasEmbeddedAiChatFlag, resolveAiChatBotEnabled, resolvedClientId])

  const openIndieFlyout = React.useCallback(() => {
    if (!isAiChatBotEnabled || isFullPageChatRoute) {
      return
    }

    setIndiePanelBypassFeatureFlag(false)
    setIndiePanelMode('flyout')
  }, [isAiChatBotEnabled, isFullPageChatRoute])

  const handleOpenAskIndie = React.useCallback(async () => {
    if (isFullPageChatRoute || !resolvedClientCode || isAiChatBotEnabled === false) {
      return
    }

    if (isAiChatBotEnabled === true) {
      openIndieFlyout()
      return
    }

    const enabled = await resolveAiChatBotEnabled()
    if (enabled) {
      setIndiePanelBypassFeatureFlag(false)
      setIndiePanelMode('flyout')
    }
  }, [isAiChatBotEnabled, isFullPageChatRoute, openIndieFlyout, resolveAiChatBotEnabled, resolvedClientCode])

  const closeIndiePanel = React.useCallback(() => {
    setIndiePanelBypassFeatureFlag(false)
    setIndiePanelMode('closed')
  }, [])

  const toggleIndieDock = React.useCallback(() => {
    setIndiePanelMode((current) => (current === 'docked' ? 'flyout' : 'docked'))
  }, [])

  const openIndieFullView = React.useCallback((handoff?: OrbitChatViewHandoff) => {
    if (!isAiChatBotEnabled || !resolvedClientCode) {
      return
    }

    const params = new URLSearchParams()
    if (handoff?.conversationId) {
      params.set('conversation_id', handoff.conversationId)
    }

    setIndiePanelMode('closed')
    navigate(
      `${buildV3ClientIndieAiPath(resolvedClientCode)}${params.toString() ? `?${params.toString()}` : ''}`,
      {
        state: {
          source: 'dashboard-ask-indie',
          orbitChatHandoff: handoff,
        },
      },
    )
  }, [isAiChatBotEnabled, navigate, resolvedClientCode])

  React.useEffect(() => {
    if (isFullPageChatRoute) {
      setIndiePanelMode('closed')
    }
  }, [isFullPageChatRoute])

  React.useEffect(() => {
    if (!isAiChatBotEnabled && !indiePanelBypassFeatureFlag && indiePanelMode !== 'closed') {
      setIndiePanelMode('closed')
    }
  }, [indiePanelBypassFeatureFlag, indiePanelMode, isAiChatBotEnabled])

  React.useEffect(() => {
    const unsubscribe = subscribeOrbitPageAssistantState((nextState: any) => {
      setPageAssistant(nextState)
    })

    const handleOpenPanelRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ source?: unknown; prompt?: unknown; submit?: unknown }>).detail
      const isOtbPromptLaunch = detail?.source === 'otb-kpi'

      if (!isFullPageChatRoute && (isAiChatBotEnabled || isOtbPromptLaunch)) {
        if (typeof detail?.prompt === 'string' && detail.prompt.trim()) {
          indiePanelPromptSequence.current += 1
          setIndiePanelPromptRequest({
            id: indiePanelPromptSequence.current,
            prompt: detail.prompt,
            submit: detail.submit === true,
          })
        }
        setIndiePanelBypassFeatureFlag(Boolean(isOtbPromptLaunch))
        setIndiePanelMode('flyout')
      }
    }

    window.addEventListener(ORBIT_OPEN_INDIE_PANEL_EVENT, handleOpenPanelRequest)

    return () => {
      unsubscribe()
      window.removeEventListener(ORBIT_OPEN_INDIE_PANEL_EVENT, handleOpenPanelRequest)
    }
  }, [isAiChatBotEnabled, isFullPageChatRoute])

  const navItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      to: buildV3ClientDashboardPath(resolvedClientCode),
      exact: true,
      matchPrefixes: [buildV3ClientDashboardPath(resolvedClientCode)],
    },
    {
      key: 'performance',
      label: 'Performance',
      to: buildV3ClientDashboardPerformancePath(resolvedClientCode),
      exact: true,
      matchPrefixes: [buildV3ClientDashboardPerformancePath(resolvedClientCode)],
    },
    {
      key: 'reports',
      label: 'Reports',
      to: buildV3ClientDashboardReportsPath(resolvedClientCode, resolvedClientId || ''),
      matchPrefixes: [buildV3ClientDashboardReportsPath(resolvedClientCode, resolvedClientId || '')],
      disabled: !resolvedClientId,
    },
    {
      key: 'data',
      label: 'Data',
      to: buildV3ClientUploadDataPath(resolvedClientCode),
      exact: true,
      matchPrefixes: [buildV3ClientUploadDataPath(resolvedClientCode)],
      disabled: !resolvedClientCode,
    },
  ]
  const askIndieHref = resolvedClientCode && (isFullPageChatRoute || isAiChatBotEnabled === true)
    ? buildV3ClientIndieAiPath(resolvedClientCode)
    : undefined
  const settingsMenuItems = getV3SettingsMenuItems(authContext)
  const profileMenuItems = resolvedClientCode
    ? [
      { key: 'locations', label: 'Locations', to: buildV3ClientLocationsPath(resolvedClientCode) },
      { key: 'categories', label: 'Categories', to: buildV3ClientCategoriesPath(resolvedClientCode) },
      { key: 'classes', label: 'Classes', to: buildV3ClientClassesPath(resolvedClientCode) },
      { key: 'pos-mapping', label: 'POS Mapping', to: buildV3ClientPosMappingPath(resolvedClientCode) },
    ]
    : []

  if (shouldShowHydrationState) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F7FB] px-6 py-12">
        <div className="text-center text-sm text-gray-500">Loading version 3 site…</div>
      </div>
    )
  }

  if (!isV3EnabledForClient) {
    return <Navigate to={buildLegacyClientDashboardPath(client_code || currentClient?.ClientCode || '')} replace />
  }

  return (
    <V3ShellLayoutProvider value={{ leftNavWidth: hasPersistentLeftNav ? PERSISTENT_LEFT_NAV_WIDTH : 0 }}>
      <div className="flex min-h-screen flex-col bg-[#F9FAFB] text-gray-900">
        {React.createElement(V3ShellHeader as any, {
          brandHref: buildV3ClientDashboardPath(resolvedClientCode),
          navItems: navItems as any,
          currentUser,
          onLogout: () => authContext?.logout?.(),
          showClientSelect: !bypassCrossClientDashboard,
          clientSelectPlaceholder: 'Select a client',
          clientSelectUnselectPath: '/v3/home',
          askIndieHref,
          onOpenAskIndie: !isFullPageChatRoute && resolvedClientCode && isAiChatBotEnabled !== false ? handleOpenAskIndie : undefined,
          profileHref: resolvedClientCode
            ? buildV3ClientProfilePath(resolvedClientCode)
            : currentUser?.id ? buildV3UserDetailPath(currentUser.id) : undefined,
          accountHref: resolvedClientCode && currentUser?.id
            ? buildV3UserDetailPath(currentUser.id)
            : undefined,
          profileMenuItems,
          showInlineNav: !isMobile,
          showNavToggle: showShellNavToggle,
          onToggleNav: () => setNavOpen((current) => !current),
          settingsMenuItems: settingsMenuItems as any,
        })}

        {shouldRenderLeftDrawer && (
          <V3LeftDrawer
            open={hasPersistentLeftNav || navOpen}
            onClose={() => setNavOpen(false)}
            topOffset={hasPersistentLeftNav ? 80 : 0}
            persistent={hasPersistentLeftNav}
            primaryNavItems={navItems as any}
            showPrimaryNavItems={isMobile}
            showPlannerNavContent={showPlannerLeftNav}
            mobileMode={isMobile}
            askIndieHref={askIndieHref}
            onOpenAskIndie={!isFullPageChatRoute && resolvedClientCode && isAiChatBotEnabled !== false ? handleOpenAskIndie : undefined}
          />
        )}

        <main
          className="min-w-0 flex-1"
          style={{
            marginLeft: hasPersistentLeftNav ? `${PERSISTENT_LEFT_NAV_WIDTH}px` : undefined,
            marginRight: activeIndiePanelMode === 'docked' || activeIndiePanelMode === 'page'
              ? `${indiePanelWidth + 24}px`
              : undefined,
            transition: 'margin-left 0.25s ease, margin-right 0.25s ease',
            overflow: isFullPageChatRoute ? 'hidden' : undefined,
          }}
        >
          <Outlet />
        </main>

        {(isAiChatBotEnabled || indiePanelBypassFeatureFlag) && activeIndiePanelMode !== 'closed' && (
          <div
            className="fixed bottom-4 right-4 z-50"
            style={{
              top: 96,
              width: 'min(calc(100vw - 32px), 460px)',
            }}
          >
            <V3IndieChatPanel
              mode={activeIndiePanelMode as any}
              clientId={resolvedClientId || 0}
              client={currentClient}
              pageAssistant={pageAssistant}
              initialPrompt={indiePanelPromptRequest?.prompt}
              promptRequestId={indiePanelPromptRequest?.id}
              autoSubmitInitialPrompt={indiePanelPromptRequest?.submit}
              onInitialPromptSubmitted={() => setIndiePanelPromptRequest(null)}
              onClose={activeIndiePanelMode === 'page' ? undefined : closeIndiePanel}
              onToggleDock={activeIndiePanelMode === 'page' ? undefined : toggleIndieDock}
              onOpenFullView={openIndieFullView}
            />
          </div>
        )}

        <ScrollToTopFab hidden={activeIndiePanelMode !== 'closed'} zIndex={51} />


        <Snackbar
          open={globalTipContext.messageOpen}
          TransitionComponent={MessageTransitionDown}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert variant="filled" severity="warning" sx={{ width: '100%' }}>
            {globalTipContext.messageContent}
          </Alert>
        </Snackbar>
        <Snackbar
          open={globalTipContext.successOpen}
          TransitionComponent={MessageTransitionDown}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert variant="filled" severity="success" sx={{ width: '100%' }}>
            {globalTipContext.successContent}
          </Alert>
        </Snackbar>
      </div>
    </V3ShellLayoutProvider>
  )
}

